import { AnchorsText } from "./AnchorsText";
import {
  type RelevantTopicAnchorRow,
  type UrlGroup,
  type TaskType,
} from "./useTaskCreation";

export const PRICE_PER_UNIQUE_ARTICLE = 0.14;

export type GroupEstimate = {
  uniqueArticles: number;
  cost: number;
};

export type RelevantTopicEstimate = {
  perAnchor: GroupEstimate[];
  totalUniqueArticles: number;
  totalCost: number;
};

export type BulkEstimate = {
  totalUniqueArticles: number;
  totalCost: number;
};

/**
 * Оценка объёма и стоимости генерации для формы создания задачи: сколько
 * уникальных статей даст группа URL и во что она обойдётся. Правила счёта
 * повторяют генерацию на бэкенде (GEN-08).
 */
export class CostEstimate {
  // Mirrors GEN-08 generation logic: max(1, floor(uniqueness% * count)) unique articles per anchor.
  public static relevantUniqueArticles(uniqueness: number, articleCount: number): number {
    const uniqueShare = (uniqueness / 100) * articleCount;
    return Math.max(1, Math.floor(uniqueShare));
  }

  // AnchorTopicTask: one anchor produces exactly one unique article; count drives spintax variations only.
  // Returns null when the textarea is empty or fails to parse, so the estimate line stays hidden.
  public static anchorTopicGroupEstimate(anchorsText: string): GroupEstimate | null {
    let anchorTopicGroupEstimateOutcome: GroupEstimate | null;
    const parsed = AnchorsText.parse(anchorsText);
    if (parsed.error !== null) {
      anchorTopicGroupEstimateOutcome = null;
    } else {
      if (parsed.anchors.length === 0) {
        anchorTopicGroupEstimateOutcome = null;
      } else {
        const uniqueArticles = parsed.anchors.length;
        anchorTopicGroupEstimateOutcome = { uniqueArticles, cost: uniqueArticles * PRICE_PER_UNIQUE_ARTICLE };
      }
    }
    return anchorTopicGroupEstimateOutcome;
  }

  public static relevantTopicGroupEstimate(rows: RelevantTopicAnchorRow[]): RelevantTopicEstimate {
    const perAnchor = rows.map((row) => {
      const uniqueArticles = CostEstimate.relevantUniqueArticles(row.uniqueness, row.articleCount);
      return { uniqueArticles, cost: uniqueArticles * PRICE_PER_UNIQUE_ARTICLE };
    });
    const totalUniqueArticles = perAnchor.reduce((sum, item) => sum + item.uniqueArticles, 0);
    return {
      perAnchor,
      totalUniqueArticles,
      totalCost: totalUniqueArticles * PRICE_PER_UNIQUE_ARTICLE,
    };
  }

  // FixedRelevantTopicTask: unique article count is explicit (uniqueArticlesCount), cost = N × price.
  public static fixedRelevantTopicGroupEstimate(uniqueArticlesCount: number): GroupEstimate | null {
    let fixedRelevantTopicGroupEstimateOutcome: GroupEstimate | null;
    if (!Number.isInteger(uniqueArticlesCount) || uniqueArticlesCount < 1) {
      fixedRelevantTopicGroupEstimateOutcome = null;
    } else {
      fixedRelevantTopicGroupEstimateOutcome = { uniqueArticles: uniqueArticlesCount, cost: uniqueArticlesCount * PRICE_PER_UNIQUE_ARTICLE };
    }
    return fixedRelevantTopicGroupEstimateOutcome;
  }

  public static sumBulkEstimate(estimates: GroupEstimate[]): BulkEstimate {
    const totalUniqueArticles = estimates.reduce((sum, item) => sum + item.uniqueArticles, 0);
    return {
      totalUniqueArticles,
      totalCost: totalUniqueArticles * PRICE_PER_UNIQUE_ARTICLE,
    };
  }

  // Total estimate across all URL-groups of the current type. null hides the bulk block:
  // for homepage tasks (no per-article cost) or when no group has a computable estimate yet.
  public static bulkEstimate(taskType: TaskType, groups: UrlGroup[]): BulkEstimate | null {
    let bulkEstimateOutcome: BulkEstimate | null;
    if (taskType === "homepage_publication" || taskType === "sitewide_publication") {
      bulkEstimateOutcome = null;
    } else {
      const estimates: GroupEstimate[] = [];
      for (const group of groups) {
        if (taskType === "generation_anchor_topic") {
          const estimate = CostEstimate.anchorTopicGroupEstimate(group.anchorsText);
          if (estimate !== null) {
            estimates.push(estimate);
          }
          continue;
        }
        if (taskType === "generation_relevant_topic_fixed") {
          const estimate = CostEstimate.fixedRelevantTopicGroupEstimate(group.uniqueArticlesCount);
          if (estimate !== null) {
            estimates.push(estimate);
          }
          continue;
        }
        if (group.anchorInputMode.mode === "table") {
          const groupEstimate = CostEstimate.relevantTopicGroupEstimate(group.anchorInputMode.rows);
          estimates.push({
            uniqueArticles: groupEstimate.totalUniqueArticles,
            cost: groupEstimate.totalCost,
          });
        }
      }
      if (estimates.length === 0) {
        bulkEstimateOutcome = null;
      } else {
        bulkEstimateOutcome = CostEstimate.sumBulkEstimate(estimates);
      }
    }
    return bulkEstimateOutcome;
  }

  public static formatCost(cost: number): string {
    return `~$${cost.toFixed(2)}`;
  }

  public static formatEstimate(uniqueArticles: number, cost: number): string {
    return `${CostEstimate.formatCost(cost)} (${uniqueArticles} уникальных статей)`;
  }
}
